import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api.js";
import type { MailSettings } from "../lib/types.js";

/**
 * "Zet e-mail op mijn telefoon" — het scherm dat iemand zonder hulp moet
 * kunnen doorlopen.
 *
 * Uitgangspunten, want dit is de plek waar mensen afhaken:
 *  - één handeling tegelijk, en maar één knop die eruit springt;
 *  - het apparaat wordt herkend, zodat er geen keuzemenu nodig is dat begint
 *    met een vraag die iemand niet kan beantwoorden;
 *  - na het downloaden staat er wát er nu op het scherm gaat gebeuren, in de
 *    woorden die iOS zelf gebruikt;
 *  - geen jargon: geen "IMAP", geen "SSL/TLS", geen "poort" tenzij iemand
 *    doorklikt naar de handmatige instellingen.
 */

interface Props {
  address: string;
  onClose: () => void;
}

type Toestel = "ios" | "android" | "desktop";

function herkenToestel(): Toestel {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // iPadOS doet zich sinds versie 13 voor als een Mac; het aanraakscherm verraadt hem.
  if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

function Kopieer({ waarde }: { waarde: string }): React.JSX.Element {
  const [gekopieerd, setGekopieerd] = useState(false);
  return (
    <button
      type="button"
      className="kopieer"
      onClick={() => {
        void navigator.clipboard?.writeText(waarde).then(() => {
          setGekopieerd(true);
          window.setTimeout(() => setGekopieerd(false), 1600);
        });
      }}
    >
      {gekopieerd ? "gekopieerd" : "kopieer"}
    </button>
  );
}

export function DeviceSetup({ address, onClose }: Props): React.JSX.Element {
  const toestel = herkenToestel();
  const [settings, setSettings] = useState<MailSettings | null>(null);
  const [metWachtwoord, setMetWachtwoord] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [gedownload, setGedownload] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [toonHandmatig, setToonHandmatig] = useState(toestel !== "ios");

  useEffect(() => {
    api.mailSettings().then(setSettings).catch(() => undefined);
  }, []);

  async function download(): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      const { url } = await api.profileTicket(metWachtwoord);
      // Navigeren en niet fetchen: de browser moet het bestand aan het
      // besturingssysteem geven, en op iOS opent Safari dan meteen het
      // installatiescherm.
      window.location.href = url;
      setGedownload(true);
    } catch (cause) {
      setFout(cause instanceof ApiError ? cause.message : "Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="composer-backdrop" role="dialog" aria-modal="true" aria-label="E-mail op uw apparaat">
      <div className="composer setup">
        <header className="composer-head">
          <h2>E-mail op uw apparaat</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten">×</button>
        </header>

        <div className="setup-body">
          <p className="setup-lead">
            Hiermee zet u <strong>{address}</strong> op uw telefoon, tablet of computer.
            U hoeft zelf niets in te stellen.
          </p>

          {toestel === "ios" && (
            <>
              <ol className="setup-stappen">
                <li>
                  <span className="setup-nr">1</span>
                  <div>
                    <h3>Tik op de knop hieronder</h3>
                    <p>Uw iPhone vraagt of hij het profiel mag downloaden. Tik op <strong>Sta toe</strong>.</p>
                  </div>
                </li>
                <li>
                  <span className="setup-nr">2</span>
                  <div>
                    <h3>Open Instellingen</h3>
                    <p>
                      Bovenaan staat nu <strong>Profiel gedownload</strong>. Tik daarop.
                      Ziet u het niet? Ga naar <strong>Algemeen → VPN en apparaatbeheer</strong>.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="setup-nr">3</span>
                  <div>
                    <h3>Tik rechtsboven op Installeer</h3>
                    <p>
                      U voert uw telefooncode in. Er verschijnt een waarschuwing dat het
                      profiel niet is ondertekend — dat is normaal. Tik nogmaals op
                      <strong> Installeer</strong>.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="setup-nr">4</span>
                  <div>
                    <h3>Klaar</h3>
                    <p>Open de Mail-app. Uw berichten staan er binnen een minuut in.</p>
                  </div>
                </li>
              </ol>

              <label className="setup-check">
                <input
                  type="checkbox"
                  checked={metWachtwoord}
                  onChange={(e) => setMetWachtwoord(e.target.checked)}
                />
                <span>
                  Wachtwoord meesturen zodat u het niet hoeft in te tikken
                  <small>
                    Aangeraden. Het bestand bevat dan uw wachtwoord — verwijder het na
                    de installatie uit uw map Downloads.
                  </small>
                </span>
              </label>

              <button
                type="button"
                className="btn btn--send setup-hoofdknop"
                onClick={() => void download()}
                disabled={bezig}
              >
                {bezig ? "Bezig…" : "Zet e-mail op deze iPhone"}
              </button>

              {gedownload && (
                <p className="setup-ok">
                  Gedownload. Ga nu naar stap 2 hierboven: open <strong>Instellingen</strong>.
                </p>
              )}
            </>
          )}

          {toestel === "android" && (
            <p className="setup-lead">
              Op Android stelt u de mail in via de app <strong>Gmail</strong> of
              <strong> Outlook</strong>: kies <em>Ander account</em> of <em>IMAP</em> en
              vul de gegevens hieronder in.
            </p>
          )}

          {toestel === "desktop" && (
            <p className="setup-lead">
              In <strong>Outlook</strong> kiest u <em>Bestand → Account toevoegen</em>,
              vult u uw e-mailadres in en kiest u bij het type account <strong>IMAP</strong>.
              Gebruik dan de gegevens hieronder.
            </p>
          )}

          {toestel === "ios" && !toonHandmatig && (
            <button type="button" className="link-button setup-meer" onClick={() => setToonHandmatig(true)}>
              Ik gebruik Outlook of een andere app
            </button>
          )}

          {toonHandmatig && settings !== null && (
            <div className="setup-gegevens">
              <h3>Gegevens om zelf in te vullen</h3>
              <dl>
                <div><dt>E-mailadres</dt><dd>{settings.address} <Kopieer waarde={settings.address} /></dd></div>
                <div><dt>Gebruikersnaam</dt><dd>{settings.address} <Kopieer waarde={settings.address} /></dd></div>
                <div><dt>Wachtwoord</dt><dd>uw eigen wachtwoord</dd></div>
              </dl>

              <h4>Inkomend (IMAP)</h4>
              <dl>
                <div><dt>Server</dt><dd>{settings.incoming.host} <Kopieer waarde={settings.incoming.host} /></dd></div>
                <div><dt>Poort</dt><dd>{settings.incoming.port}</dd></div>
                <div><dt>Beveiliging</dt><dd>{settings.incoming.security}</dd></div>
              </dl>

              <h4>Uitgaand (SMTP)</h4>
              <dl>
                <div><dt>Server</dt><dd>{settings.outgoing.host} <Kopieer waarde={settings.outgoing.host} /></dd></div>
                <div><dt>Poort</dt><dd>{settings.outgoing.port}</dd></div>
                <div><dt>Beveiliging</dt><dd>{settings.outgoing.security}</dd></div>
              </dl>

              <p className="settings-hint">
                Vraagt de app of de uitgaande server een wachtwoord nodig heeft? Antwoord
                dan <strong>ja</strong>, met dezelfde gegevens als hierboven.
              </p>
            </div>
          )}

          {fout !== null && <p className="composer-error" role="alert">{fout}</p>}
        </div>

        <footer className="composer-actions">
          <button type="button" className="btn btn--quiet" onClick={onClose}>Sluiten</button>
        </footer>
      </div>
    </div>
  );
}
